import {
    CoreGridInput,
    CoreReceivingsGridOutput,
    fromCoreReceivingNames,
    GridInput,
    GridOutput,
    ReceivedReceiving,
    Receiving,
    toCoreReceivingNames,
    Optional,
    ReceivingFileInterface,
    ReceivingFileJoinedInterface,
    ReceivingInterface,
    ReceivingResponse,
    ReceivingDetailInterface,
} from 'skladon-types';

/** Detail API row or grid row from Core (grid omits nested relations). */
type ReceivingFromCoreInput =
    | Optional<ReceivingResponse, 'PRODUCTS' | 'PROVIDER'>
    | Omit<ReceivingDetailInterface, 'PRODUCTS' | 'PROVIDER'>;

// NOTE: Receiving type intentionally does not expose internal numeric ID (ID) field directly.
// Some callers (e.g. grid mappers) still map ID to an `id` property for convenience, so we
// extend the type locally with this optional internal identifier.
type ReceivingWithInternalId = Receiving & { id?: number };

import { CoreGridMapper } from './CoreGridMapper';
import { toCoreDecimalString } from './numbers';

// Type guard to check if receiving has PRODUCTS field (full detail payload)
const isReceivingResponse = (
    receiving: ReceivingFromCoreInput
): receiving is Optional<ReceivingResponse, 'PRODUCTS' | 'PROVIDER'> =>
    'PRODUCTS' in receiving && Array.isArray((receiving as ReceivingResponse).PRODUCTS);

// Mapped receiving interface (for API responses)

// Column name mappings
// Core Grid Mapper utilities (simplified version for this package)
export class ReceivingsMapper {
    public static fromCore(receiving: ReceivingFromCoreInput): ReceivingWithInternalId {
        const isFullReceiving = isReceivingResponse(receiving);
        return {
            // ID is available only when receiving already exists in Core; for grid rows it can be undefined
            id: (receiving as ReceivingInterface).ID,
            asn: receiving.ASN,
            eshopReceivingId: receiving.ESHOP_RECEIVING_ID!,
            createdAt: String(receiving.CREATED_AT),
            asnLabelEmail: receiving.ASN_LABEL_EMAIL,
            deliveryNoteNumber: receiving.DELIVERY_NOTE_NUMBER || null,
            deliveryType: receiving.DELIVERY_TYPE || null,
            description: receiving.DESCRIPTION || null,
            driverCompany:
                receiving.DRIVER_COMPANY === 'other'
                    ? receiving.DRIVER_COMPANY_OTHER_NAME || null
                    : receiving.DRIVER_COMPANY || null,
            driverDispatcherContact: receiving.DRIVER_DISPATCHER_CONTACT || null,
            driverName: receiving.DRIVER_NAME || null,
            driverPhone: receiving.DRIVER_PHONE || null,
            driverTrailerLicensePlate: receiving.DRIVER_TRAILER_LICENSE_PLATE || null,
            duty: !!receiving.DUTY,
            estimatedDelivery: String(receiving.ESTIMATED_DELIVERY),
            products: isFullReceiving && receiving.PRODUCTS
                ? receiving.PRODUCTS.map((product) => ({
                      sku: product.SKU,
                      quantity: product.QUANTITY,
                      lot: product.LOT || null,
                      received: product.RECEIVED || null,
                      expirationDate: product.EXPIRATION_DATE || null,
                      productPrice: product.PRODUCT_PRICE || null,
                      lineState: product.LINE_STATE
                  }))
                : [],
            status: receiving.STATUS!,
            supplier: receiving.SUPPLIER || null,
            trackingNumber: receiving.TRACKING_NUMBER || null,
            unitsAmount: receiving.UNITS_AMOUNT,
            urgent: receiving.URGENT || null,
            eshopNote: receiving.ESHOP_NOTE,
            priority: receiving.PRIORITY,
            containerNumber: receiving.CONTAINER_NUMBER,
            announcedPalletsCount: receiving.ANNOUNCED_PALLETS_COUNT,
            receivingType: receiving.RECEIVING_TYPE,
            realDelivery: receiving.REAL_DELIVERY,
            slaSuspended: receiving.SLA_SUSPENDED,
            photoDocumentationTaken: receiving.PHOTO_DOCUMENTATION_TAKEN,
            deliveredPalletsCount: receiving.DELIVERED_PALLETS_COUNT,
            deliveredCartonsCount: receiving.DELIVERED_CARTONS_COUNT,
            incoterms: receiving.INCOTERMS,
            returnedEurPalletsCount: receiving.RETURNED_EUR_PALLETS_COUNT,
            deliveredStandardPalletsCount: receiving.DELIVERED_STANDARD_PALLETS_COUNT,
            returnedStandardPalletsCount: receiving.RETURNED_STANDARD_PALLETS_COUNT,
            deliveredOtherPalletsCount: receiving.DELIVERED_OTHER_PALLETS_COUNT,
            deliveredBrokenPalletsCount: receiving.DELIVERED_BROKEN_PALLETS_COUNT,
            skladonNote: receiving.SKLADON_NOTE,
            slaDate: receiving.SLA_DATE ? String(receiving.SLA_DATE) : null,
            unloadedAt: receiving.UNLOADED_AT,
            receivedAt: receiving.RECEIVED_AT,
            confirmedAt: receiving.CONFIRMED_AT,
            canceledAt: receiving.CANCELED_AT ? String(receiving.CANCELED_AT) : null,
            files: ReceivingsMapper.mapReceivingFilesToGwms(receiving.RECEIVING_FILES)
        };
    }

    private static mapReceivingFilesToGwms(
        rfList:
            | ReceivingResponse['RECEIVING_FILES']
            | ReceivingDetailInterface['RECEIVING_FILES']
            | undefined
    ) {
        if (!rfList?.length) {
            return [];
        }
        const first = rfList[0] as {
            FILE?: {
                TYPE: string;
                ORIGINAL_FILE_NAME: string;
                FILE_TYPE: string;
                FILE_SIZE: string;
                CREATED_AT?: string | Date | null;
                CREATOR_ROLE?: string | null;
            };
            CREATED_AT?: string | Date | null;
        };
        if (first.FILE) {
            return (rfList as ReceivingFileInterface[]).map((rf) => ({
                id: rf.FILE_ID,
                type: rf.FILE.TYPE,
                fileName: rf.FILE.ORIGINAL_FILE_NAME,
                fileType: rf.FILE.FILE_TYPE,
                fileSize: rf.FILE.FILE_SIZE,
                description: rf.DESCRIPTION ?? undefined,
                creatorRole: rf.FILE.CREATOR_ROLE ?? rf.CREATOR_ROLE ?? undefined,
                createdAt: rf.FILE.CREATED_AT ?? undefined
            }));
        }
        return (rfList as ReceivingFileJoinedInterface[]).map((rf) => ({
            id: rf.FILE_ID,
            type: rf.TYPE,
            fileName: rf.ORIGINAL_FILE_NAME,
            fileType: rf.FILE_TYPE,
            fileSize: rf.FILE_SIZE,
            description: rf.DESCRIPTION ?? undefined,
            creatorRole: rf.CREATOR_ROLE ?? undefined,
            createdAt: rf.CREATED_AT ?? undefined
        }));
    }

    public static toCore(receiving: Receiving): ReceivedReceiving {
        return {
            ESHOP_RECEIVING_ID: receiving.eshopReceivingId,
            DELIVERY_NOTE_NUMBER: receiving.deliveryNoteNumber,
            DELIVERY_TYPE: receiving.deliveryType === 'containter' ? 'container' : receiving.deliveryType,
            DESCRIPTION: receiving.description,
            DRIVER_COMPANY: 'other',
            DRIVER_COMPANY_OTHER_NAME: receiving.driverCompany,
            DRIVER_DISPATCHER_CONTACT: receiving.driverDispatcherContact,
            DRIVER_NAME: receiving.driverName,
            DRIVER_PHONE: receiving.driverPhone,
            DRIVER_TRAILER_LICENSE_PLATE: receiving.driverTrailerLicensePlate,
            DUTY: receiving.duty !== undefined ? Number(receiving.duty) : undefined,
            ESTIMATED_DELIVERY: receiving.estimatedDelivery,
            PRODUCTS: receiving.products  ? receiving.products.map((product) => ({
                SKU: product.sku,
                NAME: product.sku,
                QUANTITY: product.quantity,
                LOT: product.lot || undefined,
                EXPIRATION_DATE: product.expirationDate || undefined,
                PRODUCT_PRICE: toCoreDecimalString(product.productPrice),
                LINE_STATE: product.lineState
            })) : undefined,
            STATUS: receiving.status || undefined,
            SUPPLIER: receiving.supplier,
            TRACKING_NUMBER: receiving.trackingNumber,
            UNITS_AMOUNT: receiving.unitsAmount,
            ESHOP_NOTE: receiving.eshopNote,
            PRIORITY: receiving.priority,
            CONTAINER_NUMBER: receiving.containerNumber,
            ANNOUNCED_PALLETS_COUNT: receiving.announcedPalletsCount,
            RECEIVING_TYPE: receiving.receivingType,
            INCOTERMS: receiving.incoterms,
            ASN_LABEL_EMAIL: receiving.asnLabelEmail,
            FILES: Array.isArray(receiving.files)
                ? receiving.files.length
                    ? receiving.files.map((f) => ({
                          id: f.id,
                          data: f.data,
                          fileName: f.fileName,
                          type: f.type,
                          fileType: f.fileType,
                          fileSize: f.fileSize,
                          description: f.description
                      }))
                    : []
                : undefined
        };
    }

    public static toCorePatch(receiving: Partial<Receiving>): Partial<ReceivedReceiving> {
        const core = ReceivingsMapper.toCore(receiving as Receiving);
        return Object.fromEntries(
            Object.entries(core).filter(([key, value]) => {
                if (value === undefined) {
                    return false;
                }
                if (
                    receiving.driverCompany === undefined
                    && (key === 'DRIVER_COMPANY' || key === 'DRIVER_COMPANY_OTHER_NAME')
                ) {
                    return false;
                }
                return true;
            })
        ) as Partial<ReceivedReceiving>;
    }

    public static fromCoreGrid(grid: CoreReceivingsGridOutput): GridOutput {
        return {
            data: grid.data
                .map((coreReceiving) => ({
                    ...ReceivingsMapper.fromCore(coreReceiving),
                    driverCompanyOtherName: coreReceiving.DRIVER_COMPANY_OTHER_NAME ?? null,
                    quantity: coreReceiving.QUANTITY ?? null,
                    received: coreReceiving.RECEIVED ?? null,
                    diffQuantity: coreReceiving.DIFF_QUANTITY ?? null
                }))
                .map((receiving) =>
                    CoreGridMapper.omitColumns(receiving, fromCoreReceivingNames, grid.request.columns)
                ),
            request: CoreGridMapper.mapGrid(grid.request, fromCoreReceivingNames, 1000, ['duty']),
            totalCount: grid.totalCount
        };
    }

    public static toCoreGrid(grid: GridInput): CoreGridInput {
        return CoreGridMapper.mapGrid(grid, toCoreReceivingNames, 1000);
    }
}
